import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  updateUserStatus,
  setSelectedUser,
  clearSelectedUser,
} from '../store/slices/usersSlice';
import UserList from '../components/users/UserList';
import UserForm from '../components/users/UserForm';
import UserFilters from '../components/users/UserFilters';
import BulkUserUpload from '../components/users/BulkUserUpload';
import ScrollHeader from '../components/common/ScrollHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import FiltersSkeleton from '../components/common/FiltersSkeleton';
import { User, UserFormData, FetchUsersParams } from '../types';
import { PlusIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { usersApi } from '../services/api';

const Users = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { users, selectedUser, isLoading, error } = useAppSelector((state) => state.users);
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    role: '',
    org_id: searchParams.get('org_id') || '',
  });
  const [orgUserCount, setOrgUserCount] = useState<number>(0);
  const [orgMaxUsers, setOrgMaxUsers] = useState<number>(0);

  const loadUsers = (page = 1) => {
    const params: FetchUsersParams = {
      skip: (page - 1) * pageSize,
      limit: pageSize,
      search: filters.search,
      status: filters.status,
      role: filters.role,
    };

    if (filters.org_id) {
      params.org_id = parseInt(filters.org_id, 10);
    }

    dispatch(fetchUsers(params));
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    loadUsers(1); // Reset to first page when changing page size
  };

  useEffect(() => {
    loadUsers();
  }, [filters, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update filters when URL params change
  useEffect(() => {
    const orgId = searchParams.get('org_id');
    if (orgId) {
      setFilters(prev => ({ ...prev, org_id: orgId }));
    }
  }, [searchParams]);

  // Load organization data for org admins from JWT token
  useEffect(() => {
    const loadOrgData = async () => {
      if (currentUser?.role === 'org_admin') {
        try {
          console.log('Loading org data for org admin, currentUser:', currentUser);
          
          // Check if organization data is available in JWT token
          if (currentUser.organization_id && currentUser.max_users) {
            console.log('Using organization data from JWT token');
            setOrgMaxUsers(currentUser.max_users);
            
            // Load user count for the organization
            const userResponse = await usersApi.getUsers({ org_id: currentUser.organization_id, limit: 1 });
            if (userResponse.data && userResponse.data.meta) {
              setOrgUserCount(userResponse.data.meta.total);
              console.log('Loaded user count:', userResponse.data.meta.total);
              console.log('Max users from JWT:', currentUser.max_users);
            }
          } else {
            console.log('No organization data in JWT token, user may need to re-login');
          }
        } catch (error) {
          console.error('Failed to load org data:', error);
        }
      }
    };
    
    loadOrgData();
  }, [currentUser]);

  const refreshData = async () => {
    // Refresh users list
    loadUsers();
    
    // Refresh org data for org admins
    if (currentUser?.role === 'org_admin' && currentUser.organization_id) {
      try {
        const userResponse = await usersApi.getUsers({ org_id: currentUser.organization_id, limit: 1 });
        if (userResponse.data && userResponse.data.meta) {
          setOrgUserCount(userResponse.data.meta.total);
        }
      } catch (error) {
        console.error('Failed to refresh org data:', error);
      }
    }
  };

  const handleSubmit = async (formData: UserFormData) => {
    try {
      if (selectedUser) {
        const updateData = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          status: formData.status,
          organization_id: formData.organization_id,
          organization_role: formData.organization_role,
          credit_limit: formData.credit_limit,
          access_type: formData.access_type
        };
        await dispatch(updateUser({ id: selectedUser.user_id, data: updateData })).unwrap();
      } else {
        const createData = {
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          organization_id: formData.organization_id,
          organization_role: formData.organization_role,
          credit_limit: formData.credit_limit || 100,
          access_type: formData.access_type || ['web', 'ide'],
          sign_up_origin: formData.sign_up_origin
        };
        await dispatch(createUser(createData)).unwrap();
      }
      setShowForm(false);
      dispatch(clearSelectedUser());
      await refreshData();
    } catch (err) {
      console.error('Failed to save user:', err);
    }
  };

  const handleEdit = (user: User) => {
    dispatch(setSelectedUser(user));
    setShowForm(true);
  };

  const handleDelete = async (userId: number) => {
    try {
      await dispatch(deleteUser(userId)).unwrap();
      await refreshData();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const handleStatusChange = async (userId: number, status: string) => {
    try {
      await dispatch(updateUserStatus({ id: userId, status })).unwrap();
      await refreshData();
    } catch (err) {
      console.error('Failed to update user status:', err);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading && !users) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div className="h-7 w-1/4 bg-light-hover dark:bg-dark-hover rounded-lg animate-pulse" />
          <div className="w-32 h-10 bg-light-hover dark:bg-dark-hover rounded-lg animate-pulse" />
        </div>
        <FiltersSkeleton />
        <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border overflow-hidden">
          <TableSkeleton rows={10} columns={6} />
        </div>
      </div>
    );
  }

  // Get organization_id for org_admin
  const orgAdminOrgId = currentUser?.role === 'org_admin' 
    ? currentUser.organizations?.[0]?.organization?.org_id 
    : undefined;

  // Debug info - check what values we have
  const isOrgAdmin = currentUser?.role === 'org_admin';
  const isAtLimit = isOrgAdmin && orgMaxUsers > 0 && orgUserCount >= orgMaxUsers;
  
  // Add debug logging in render
  console.log('Debug - User Button Info:', {
    currentUserRole: currentUser?.role,
    isOrgAdmin,
    orgUserCount,
    orgMaxUsers,
    isAtLimit,
    currentUserOrgId: currentUser?.organization_id,
    userOrganizations: currentUser?.organizations
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <ScrollHeader title="Users" />
        <div className="flex items-center gap-3">
          {(currentUser?.role === 'super_admin' || currentUser?.role === 'org_admin') && (
            <button
              onClick={() => setShowBulkUpload(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-700 dark:text-gray-200 hover:bg-light-hover dark:hover:bg-dark-hover transition-colors duration-200"
            >
              <ArrowUpTrayIcon className="w-5 h-5" />
              Bulk Upload
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => {
                dispatch(clearSelectedUser());
                setShowForm(true);
              }}
              disabled={isAtLimit}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl transition-colors duration-200 ${
                isAtLimit
                  ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
              title={
                isAtLimit
                  ? 'Reached max allowed users'
                  : ''
              }
            >
              <PlusIcon className="w-5 h-5" />
              Add User
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500">
          {error}
        </div>
      )}

      {showForm ? (
        <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
          <UserForm
            user={selectedUser || undefined}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              dispatch(clearSelectedUser());
            }}
          />
        </div>
      ) : (
        <>
          <UserFilters
            onSearchChange={(search) => handleFilterChange('search', search)}
            onStatusChange={(status) => handleFilterChange('status', status)}
            onRoleChange={(role) => handleFilterChange('role', role)}
            onOrgChange={(org_id) => handleFilterChange('org_id', org_id)}
            currentFilters={filters}
            currentUserRole={currentUser?.role}
          />
          <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border overflow-hidden">
            {isLoading ? (
              <TableSkeleton rows={10} columns={6} />
            ) : users && users.items.length > 0 ? (
              <UserList
                users={users}
                onEdit={handleEdit}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onPageChange={(page) => loadUsers(page)}
                onPageSizeChange={handlePageSizeChange}
                currentPage={users.page || 1}
                pageSize={pageSize}
                currentUserRole={currentUser?.role}
              />
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No users found
              </div>
            )}
          </div>
        </>
      )}

      {showBulkUpload && (
        <BulkUserUpload
          onClose={() => setShowBulkUpload(false)}
          onSuccess={async () => {
            setShowBulkUpload(false);
            await refreshData();
          }}
          organizationId={orgAdminOrgId}
        />
      )}
    </div>
  );
};

export default Users;
